/**
 * CommentsThread — branch-scoped threaded comments for any DAG element.
 *
 * Renders a threaded list of comments with reply, resolve, edit,
 * emoji reactions, and @mention rendering.
 */

import { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Popover,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import {
  useComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useToggleReaction,
} from "../../hooks/useComments";
import type { Comment, CommentAnchor, Reaction } from "../../api/comments";
import MentionTextArea from "./MentionTextArea";

interface CommentsThreadProps {
  projectId: string;
  targetType: string;
  targetId: string;
  branch: string;
  includeAllBranches?: boolean;
  onAnchorClick?: (anchor: CommentAnchor) => void;
}

const EMOJI_MAP: Record<string, string> = {
  thumbsup: "\u{1F44D}",
  thumbsdown: "\u{1F44E}",
  heart: "\u2764\uFE0F",
  eyes: "\u{1F440}",
  rocket: "\u{1F680}",
  thinking: "\u{1F914}",
};

const EMOJI_LIST = Object.keys(EMOJI_MAP);

function anchorLabel(anchor: CommentAnchor): string {
  switch (anchor.type) {
    case "plot_point":
      return `\u{1F4CD} (${anchor.x_value}, ${anchor.y_value})${anchor.series ? ` on ${anchor.series}` : ""}`;
    case "surface_point":
      return `\u{1F4CD} (${anchor.x.toFixed(2)}, ${anchor.y.toFixed(2)}, ${anchor.z.toFixed(2)})`;
    case "cell":
      return `\u{1F4CD} Row ${anchor.row_index}, ${anchor.column}`;
  }
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Parse @[Display Name](user_id) tokens and render as highlighted spans. */
function renderBody(body: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(body)) !== null) {
    if (match.index > last) {
      parts.push(body.slice(last, match.index));
    }
    parts.push(
      <Text key={key++} size="2" color="blue" weight="medium" style={{ cursor: "default" }}>
        @{match[1]}
      </Text>,
    );
    last = match.index + match[0].length;
  }
  if (last < body.length) {
    parts.push(body.slice(last));
  }
  return parts;
}

interface ReactionBarProps {
  reactions: Reaction[];
  currentUserId?: string;
  onToggle: (emoji: string) => void;
}

function ReactionBar({ reactions, currentUserId, onToggle }: ReactionBarProps) {
  return (
    <Flex gap="1" mt="1" align="center" wrap="wrap">
      {reactions.map((r) => {
        const reacted = currentUserId ? r.user_ids.includes(currentUserId) : false;
        return (
          <button
            key={r.emoji}
            onClick={() => onToggle(r.emoji)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              padding: "1px 6px",
              fontSize: 12,
              borderRadius: 10,
              border: reacted ? "1px solid var(--blue-8)" : "1px solid var(--gray-a5)",
              background: reacted ? "var(--blue-a3)" : "transparent",
              cursor: "pointer",
              lineHeight: "18px",
            }}
            title={`${r.emoji} (${r.count})`}
          >
            <span>{EMOJI_MAP[r.emoji] ?? r.emoji}</span>
            <span style={{ color: "var(--gray-11)", fontWeight: 500 }}>{r.count}</span>
          </button>
        );
      })}
      <Popover.Root>
        <Popover.Trigger>
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: 10,
              border: "1px solid var(--gray-a5)",
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
              color: "var(--gray-9)",
            }}
            title="Add reaction"
          >
            +
          </button>
        </Popover.Trigger>
        <Popover.Content size="1" style={{ padding: 4 }}>
          <Flex gap="1">
            {EMOJI_LIST.map((e) => (
              <button
                key={e}
                onClick={() => onToggle(e)}
                style={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 16,
                }}
                title={e}
              >
                {EMOJI_MAP[e]}
              </button>
            ))}
          </Flex>
        </Popover.Content>
      </Popover.Root>
    </Flex>
  );
}

interface CommentNodeProps {
  comment: Comment;
  replies: Comment[];
  allComments: Comment[];
  depth: number;
  projectId: string;
  currentUserId?: string;
  onReply: (parentId: string) => void;
  onAnchorClick?: (anchor: CommentAnchor) => void;
  onResolve: (commentId: string, resolved: boolean) => void;
  onDelete: (commentId: string) => void;
  onReaction: (commentId: string, emoji: string) => void;
}

function CommentNode({
  comment,
  replies,
  allComments,
  depth,
  projectId,
  currentUserId,
  onReply,
  onAnchorClick,
  onResolve,
  onDelete,
  onReaction,
}: CommentNodeProps) {
  const isAuthor = currentUserId === comment.author_id;
  const initials = (comment.author_name ?? comment.author_id)
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box pl={depth > 0 ? "4" : "0"} style={depth > 0 ? { borderLeft: "2px solid var(--gray-a5)" } : undefined}>
      <Flex gap="2" py="2">
        <Avatar size="1" fallback={initials} src={comment.author_avatar ?? undefined} radius="full" />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Flex align="center" gap="2" wrap="wrap">
            <Text size="1" weight="bold">{comment.author_name ?? comment.author_id}</Text>
            <Text size="1" color="gray">{timeAgo(comment.created_at)}</Text>
            {comment.merged_from && (
              <Badge size="1" color="violet" variant="soft">from {comment.merged_from}</Badge>
            )}
            {comment.resolved && (
              <Badge size="1" color="green" variant="soft">resolved</Badge>
            )}
          </Flex>

          {comment.anchor && (
            <Text
              size="1"
              color="blue"
              style={{ cursor: onAnchorClick ? "pointer" : undefined }}
              onClick={() => onAnchorClick?.(comment.anchor!)}
            >
              {anchorLabel(comment.anchor)}
            </Text>
          )}

          <Text size="2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }} mt="1">
            {renderBody(comment.body)}
          </Text>

          <Flex gap="2" mt="1">
            <Button size="1" variant="ghost" color="gray" onClick={() => onReply(comment.id)}>
              Reply
            </Button>
            {!comment.resolved && (
              <Button size="1" variant="ghost" color="green" onClick={() => onResolve(comment.id, true)}>
                Resolve
              </Button>
            )}
            {comment.resolved && (
              <Button size="1" variant="ghost" color="amber" onClick={() => onResolve(comment.id, false)}>
                Reopen
              </Button>
            )}
            {isAuthor && (
              <Button size="1" variant="ghost" color="red" onClick={() => onDelete(comment.id)}>
                Delete
              </Button>
            )}
          </Flex>

          <ReactionBar
            reactions={comment.reactions ?? []}
            currentUserId={currentUserId}
            onToggle={(emoji) => onReaction(comment.id, emoji)}
          />
        </Box>
      </Flex>

      {replies.map((reply) => (
        <CommentNode
          key={reply.id}
          comment={reply}
          replies={allComments.filter((c) => c.parent_id === reply.id)}
          allComments={allComments}
          depth={depth + 1}
          projectId={projectId}
          currentUserId={currentUserId}
          onReply={onReply}
          onAnchorClick={onAnchorClick}
          onResolve={onResolve}
          onDelete={onDelete}
          onReaction={onReaction}
        />
      ))}
    </Box>
  );
}

export default function CommentsThread({
  projectId,
  targetType,
  targetId,
  branch,
  includeAllBranches,
  onAnchorClick,
}: CommentsThreadProps) {
  const { data, isLoading } = useComments(projectId, targetType, targetId, branch, {
    includeAllBranches,
  });
  const createMutation = useCreateComment(projectId);
  const updateMutation = useUpdateComment(projectId);
  const deleteMutation = useDeleteComment(projectId);
  const reactionMutation = useToggleReaction(projectId);

  const [newBody, setNewBody] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  const comments = data?.comments ?? [];

  const { roots, childMap } = useMemo(() => {
    const roots: Comment[] = [];
    const childMap = new Map<string, Comment[]>();
    for (const c of comments) {
      if (!c.parent_id) {
        roots.push(c);
      } else {
        const siblings = childMap.get(c.parent_id) ?? [];
        siblings.push(c);
        childMap.set(c.parent_id, siblings);
      }
    }
    return { roots, childMap };
  }, [comments]);

  const visibleRoots = showResolved ? roots : roots.filter((c) => !c.resolved);
  const resolvedCount = roots.filter((c) => c.resolved).length;

  const handleSubmit = useCallback(() => {
    if (!newBody.trim()) return;
    createMutation.mutate(
      {
        target_type: targetType,
        target_id: targetId,
        branch,
        body: newBody.trim(),
        parent_id: replyTo ?? undefined,
      },
      {
        onSuccess: () => {
          setNewBody("");
          setReplyTo(null);
        },
      },
    );
  }, [newBody, replyTo, targetType, targetId, branch, createMutation]);

  const handleResolve = useCallback(
    (commentId: string, resolved: boolean) => {
      updateMutation.mutate({ commentId, resolved });
    },
    [updateMutation],
  );

  const handleDelete = useCallback(
    (commentId: string) => {
      deleteMutation.mutate(commentId);
    },
    [deleteMutation],
  );

  const handleReaction = useCallback(
    (commentId: string, emoji: string) => {
      reactionMutation.mutate({ commentId, emoji });
    },
    [reactionMutation],
  );

  if (isLoading) {
    return (
      <Box py="3">
        <Text size="1" color="gray">Loading comments...</Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" style={{ height: "100%" }}>
      <Box style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {visibleRoots.length === 0 && !showResolved && (
          <Text size="1" color="gray" mb="2">
            {resolvedCount > 0 ? "All threads resolved." : "No comments yet."}
          </Text>
        )}

        {visibleRoots.map((root) => (
          <CommentNode
            key={root.id}
            comment={root}
            replies={childMap.get(root.id) ?? []}
            allComments={comments}
            depth={0}
            projectId={projectId}
            onReply={(parentId) => setReplyTo(parentId)}
            onAnchorClick={onAnchorClick}
            onResolve={handleResolve}
            onDelete={handleDelete}
            onReaction={handleReaction}
          />
        ))}

        {resolvedCount > 0 && (
          <Button
            size="1"
            variant="ghost"
            color="gray"
            onClick={() => setShowResolved(!showResolved)}
            mb="2"
          >
            {showResolved ? "Hide" : "Show"} {resolvedCount} resolved thread{resolvedCount !== 1 ? "s" : ""}
          </Button>
        )}
      </Box>

      {/* Compose box — pinned to bottom */}
      <Box pt="2" style={{ borderTop: "1px solid var(--gray-a4)", flexShrink: 0 }}>
        {replyTo && (
          <Flex align="center" gap="2" mb="1">
            <Text size="1" color="blue">Replying to comment</Text>
            <Button size="1" variant="ghost" color="gray" onClick={() => setReplyTo(null)}>
              Cancel
            </Button>
          </Flex>
        )}
        <MentionTextArea
          projectId={projectId}
          value={newBody}
          onChange={setNewBody}
          placeholder={replyTo ? "Write a reply..." : "Add a comment..."}
          onSubmit={handleSubmit}
        />
        <Flex justify="end" mt="1">
          <Tooltip content="Cmd+Enter to submit">
            <Button
              size="1"
              disabled={!newBody.trim() || createMutation.isPending}
              onClick={handleSubmit}
            >
              {createMutation.isPending ? "Posting..." : "Comment"}
            </Button>
          </Tooltip>
        </Flex>
      </Box>
    </Flex>
  );
}
